import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import React from 'react';
import { AppState } from '../../AppState';
import { useAttachment } from '../../contexts/AttachmentContext';
import { AttachmentPanel } from './AttachmentPanel';
import { ChatRoom } from './ChatRoom';
import { ChatRoomHeader } from './ChatRoomHeader';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { ChatRoomInlineHeader } from './ChatRoomInlineHeader';
import { FeedbackDialogOverlay } from '../shared/FeedbackDialogOverlay';
import { ChatSidebar } from './ChatSidebar';
import { AnnouncementBanner } from './AnnouncementBanner';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'var(--app-shadow-card)',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    contentColumn: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: '0',
        height: '100%',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        paddingLeft: '0',
    },
    chatRoom: {
        flex: 1,
        minWidth: '0',
        maxWidth: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    },
    chatRoomAttachment: {
        maxWidth: '20%',
    },
    attachment: {
        width: '0',
        transition: 'width 0.3s ease-in-out',
        maxWidth: '80%',
        minWidth: '0',
        overflow: 'hidden',
        flex: '1 1 auto',
    },
    attachmentVisible: {
        width: '80%',
    },
    header: {
        borderBottom: '1px solid rgb(0 0 0 / 10%)',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        backgroundColor: tokens.colorNeutralBackground4,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
    },
    popoverHeader: {
        margin: '0',
        paddingBottom: tokens.spacingVerticalXXS,
        fontStyle: 'normal',
        fontWeight: '600',
    },
    popover: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: tokens.spacingVerticalXXL,
        gap: tokens.spacingVerticalMNudge,
        width: '398px',
    },
    input: {
        width: '100%',
    },
    buttons: {
        display: 'flex',
        alignSelf: 'end',
        gap: tokens.spacingVerticalS,
    },
    alerts: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0 72px',
    },
});

interface IChatWindowProps {
    setAppState: React.Dispatch<React.SetStateAction<AppState>>;
}

export const ChatWindow: React.FC<IChatWindowProps> = ({ setAppState }) => {
    const classes = useClasses();
    const { activeAttachment, isAttachmentVisible } = useAttachment();
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);

    return (
        <div className={classes.root}>
            <FeedbackDialogOverlay />
            {!embeddedMode && <ChatRoomHeader setAppState={setAppState} />}
            <ChatRoomInlineHeader setAppState={setAppState} isVisible={embeddedMode} />
            <div className={classes.mainContainer}>
                <ChatSidebar />
                <div className={classes.contentColumn}>
                    <AnnouncementBanner />
                    <div className={classes.content}>
                        {isAttachmentVisible && (
                            <div className={mergeClasses(classes.attachment, classes.attachmentVisible)}>
                                <AttachmentPanel
                                    attachment={activeAttachment.attachment}
                                    message={activeAttachment.message}
                                />
                            </div>
                        )}
                        <div
                            className={
                                isAttachmentVisible
                                    ? mergeClasses(classes.chatRoom, classes.chatRoomAttachment)
                                    : classes.chatRoom
                            }
                        >
                            <ChatRoom />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
